import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button, Card, Cluster, FormField, Grid, Input, Select, Stack } from "@salesapp/ui";
import { TipHeading, TipKicker } from "./AdminTip";
import { api, ApiError } from "./api";
import { countryLabel, formatDurationMs, formatIp, formatUtc } from "./format";
import { Flash } from "./Flash";

interface PageHit {
  path: string;
  duration_ms: number;
  entered_at?: string | null;
}

interface VisitSession {
  session_id: string;
  started_at: string | null;
  last_seen_at: string | null;
  source_ip: string | null;
  country: string | null;
  landing_path: string | null;
  referrer_host: string | null;
  utm_source: string | null;
  viewed_pricing: boolean;
  signed_up_at: string | null;
  org_id: string | null;
  page_count: number;
  total_ms: number;
  last_path: string | null;
  pages: PageHit[];
  videos?: VideoProgress[];
  outcome: string;
}

interface VideoProgress {
  video: string;
  watched_ms: number;
  max_pct: number;
  completed: boolean;
}

interface VideoView {
  session_id: string;
  played_at: string | null;
  source_ip: string | null;
  country: string | null;
  watched_ms: number;
  max_pct: number;
  completed: boolean;
  org_id: string | null;
  outcome: string;
}

interface WalkthroughStats {
  video: string;
  viewers: number;
  completed: number;
  signed_up: number;
  avg_watched_ms: number;
  avg_pct: number;
  views: VideoView[];
}

interface PathGroup {
  pages: string[];
  durations_ms: number[];
  total_ms: number;
  avg_ms: number;
  count: number;
  outcome: string;
}

interface WebsiteOverview {
  window_days: number;
  kpis: {
    sessions: number;
    pageviews: number;
    signups: number;
    signup_rate: number;
    pricing_views: number;
    pricing_exits: number;
    left_after_pricing: number;
    signed_up_after_pricing: number;
    pricing_to_signup: number;
    converting_avg_ms: number;
    lost_avg_ms: number;
  };
  hits_per_day: Array<{ day: string; sessions: number; pageviews: number; signups: number }>;
  countries: Array<{ country: string; sessions: number; signups: number; pageviews: number }>;
  pages: Array<{ path: string; views: number; avg_ms: number; exits: number; signed_up_after: number }>;
  pricing: {
    sessions: number;
    viewed_pricing: number;
    left_on_pricing: number;
    left_after_pricing: number;
    signed_up_after_pricing: number;
  };
  converting_paths: PathGroup[];
  drop_off_paths: PathGroup[];
  walkthrough?: WalkthroughStats;
  sessions: VisitSession[];
  total: number;
}

const OUTCOME_LABEL: Record<string, string> = {
  signed_up: "Signed up",
  pricing_exit: "Left on pricing",
  left_after_pricing: "Left after pricing",
  bounced: "One page",
  exited: "Left",
  browsing: "Still on site",
};

function pct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function outcomeClass(outcome: string): string {
  if (outcome === "signed_up") return "is-ok";
  if (outcome === "pricing_exit" || outcome === "left_after_pricing") return "is-warning";
  if (outcome === "bounced") return "is-idle";
  return "is-info";
}

function websitePath(filters: { days: string; country: string; outcome: string; ip: string }): string {
  const params = new URLSearchParams();
  params.set("days", filters.days);
  params.set("limit", "50");
  if (filters.country) params.set("country", filters.country);
  if (filters.outcome) params.set("outcome", filters.outcome);
  if (filters.ip) params.set("source_ip", filters.ip);
  return `/operator/website?${params.toString()}`;
}

export function WebsitePage() {
  const [data, setData] = useState<WebsiteOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState("30");
  const [country, setCountry] = useState("");
  const [outcome, setOutcome] = useState("");
  const [ipInput, setIpInput] = useState("");
  const [ip, setIp] = useState("");

  const filters = useMemo(() => ({ days, country, outcome, ip }), [days, country, outcome, ip]);
  const filtersDirty = days !== "30" || country !== "" || outcome !== "" || ipInput.trim() !== "";

  function clearFilters() {
    setDays("30");
    setCountry("");
    setOutcome("");
    setIpInput("");
    setIp("");
  }

  useEffect(() => {
    const handle = window.setTimeout(() => setIp(ipInput.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [ipInput]);

  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      setError(null);
      try {
        const row = await api.get<WebsiteOverview>(websitePath(filters));
        if (!cancelled) setData(row);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Website visits could not be loaded.");
        }
      }
    }
    void refresh();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  if (!data) {
    return error ? <p className="admin-error">{error}</p> : <p>Loading…</p>;
  }

  const { kpis, pricing } = data;
  const countryOptions = data.countries.map((row) => row.country);

  return (
    <Stack gap={5}>
      <Cluster gap={3} align="center">
        <h2>Website</h2>
        <p className="admin-muted">
          First-party visits from saleslox.com · last {data.window_days} days. IP is the visitor
          address behind the reverse proxy; country comes from the edge or GeoIP. Older rows that
          show a Docker address were recorded before that lookup was trusted.
        </p>
      </Cluster>
      {error && <Flash tone="error">{error}</Flash>}

      <Cluster gap={3} className="admin-threat-filters" align="end">
        <FormField
          label="Window"
          tooltip="How far back to include visits. KPIs, charts, and the visitor list all use this range."
        >
          <Select value={days} onChange={(event) => setDays(event.target.value)} aria-label="Window days">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </Select>
        </FormField>
        <FormField
          label="Country"
          tooltip="Limit to sessions whose edge/GeoIP country matches. Empty means every country in the window."
        >
          <Select value={country} onChange={(event) => setCountry(event.target.value)} aria-label="Filter country">
            <option value="">All countries</option>
            {countryOptions.map((code) => (
              <option key={code} value={code}>{countryLabel(code)}</option>
            ))}
          </Select>
        </FormField>
        <FormField
          label="Outcome"
          tooltip="How the visit ended — trial signup, left on pricing, bounce, and so on. Use this to isolate converting or lost journeys."
        >
          <Select value={outcome} onChange={(event) => setOutcome(event.target.value)} aria-label="Filter outcome">
            <option value="">All visits</option>
            {Object.entries(OUTCOME_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
        </FormField>
        <FormField
          label="IP"
          tooltip="Exact match on visitor IP (the address behind the reverse proxy). Typing debounces briefly before refreshing."
        >
          <Input value={ipInput} onChange={(event) => setIpInput(event.target.value)} aria-label="Filter IP" placeholder="203.0.113.10" />
        </FormField>
        <FormField
          label="Filters"
          tooltip="Reset window, country, outcome, and IP back to the defaults for this page."
        >
          <Button type="button" variant="secondary" disabled={!filtersDirty} onClick={clearFilters}>
            Clear
          </Button>
        </FormField>
      </Cluster>

      <Grid columns={4} gap={4}>
        <KpiTile
          label="Sessions"
          tip="Distinct first-party visit sessions in this window. Page views under the tile are total hits across those sessions."
          value={kpis.sessions}
          hint={`${kpis.pageviews} page views`}
        />
        <KpiTile
          label="Signup rate"
          tip="Share of sessions that opened a trial. Absolute trial count is shown under the percentage."
          value={pct(kpis.signup_rate)}
          hint={`${kpis.signups} trials from this window`}
        />
        <KpiTile
          label="Saw pricing"
          tip="Sessions that opened the pricing page. The hint counts those that exited while still on pricing."
          value={kpis.pricing_views}
          hint={`${kpis.pricing_exits} left on the pricing page`}
        />
        <KpiTile
          label="Pricing → trial"
          tip="Of sessions that saw pricing, the share that later signed up. Use with the pricing deterrent card below."
          value={pct(kpis.pricing_to_signup)}
          hint={`${kpis.signed_up_after_pricing} signed up after seeing price`}
        />
      </Grid>

      <Grid columns={2} gap={4}>
        <Card>
          <Stack gap={3}>
            <Cluster gap={3} align="center">
              <TipHeading
                label="Hits per day"
                tip="Daily session and page-view totals in UTC across the selected window. Empty days stay on the axis so changing 7 / 30 / 90 days reshapes the chart."
              />
              <p className="admin-muted">{data.window_days} days · sessions and page views, UTC</p>
            </Cluster>
            <HitsChart points={data.hits_per_day} />
          </Stack>
        </Card>
        <Card>
          <Stack gap={3}>
            <TipHeading
              label="By country"
              tip="Sessions, trials, and page views rolled up by visitor country for the active filters."
            />
            <table>
              <thead>
                <tr><th>Country</th><th>Sessions</th><th>Signups</th><th>Pages</th></tr>
              </thead>
              <tbody>
                {data.countries.length === 0 && (
                  <tr><td colSpan={4} className="admin-muted">No visits in this window yet. Open the marketing site to record a session.</td></tr>
                )}
                {data.countries.map((row) => (
                  <tr key={row.country}>
                    <td>{countryLabel(row.country)}</td>
                    <td>{row.sessions}</td>
                    <td>{row.signups}</td>
                    <td>{row.pageviews}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Stack>
        </Card>
      </Grid>

      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Was pricing the deterrent?"
            tip="Breakdown of who opened pricing, left on it, left after it, or signed up. Average times compare converting vs lost journeys."
          />
          <p className="admin-muted">
            {pricing.viewed_pricing} sessions opened pricing.
            {` `}{pricing.left_on_pricing} left on that page.
            {` `}{pricing.left_after_pricing} saw price and left without a trial.
            {` `}{pricing.signed_up_after_pricing} signed up after seeing it.
            Converting visits averaged {formatDurationMs(kpis.converting_avg_ms)}; visits that left averaged {formatDurationMs(kpis.lost_avg_ms)}.
          </p>
        </Stack>
      </Card>

      <Grid columns={2} gap={4}>
        <Card>
          <Stack gap={3}>
            <TipHeading
              label="Pages that lead to a trial"
              tip="Most common path sequences taken before a signup, with average time on each page."
            />
            <p className="admin-muted">Paths people actually took before signing up, with time on each page.</p>
            <PathList rows={data.converting_paths} empty="No trials with a recorded path yet." />
          </Stack>
        </Card>
        <Card>
          <Stack gap={3}>
            <TipHeading
              label="Saw pricing, then left"
              tip="Journeys that hit pricing and did not convert — the pricing-deterrent and content drop-off set."
            />
            <p className="admin-muted">These journeys are the pricing-deterrent and content drop-off set.</p>
            <PathList rows={data.drop_off_paths} empty="No pricing drop-off journeys in this window." />
          </Stack>
        </Card>
      </Grid>

      {data.walkthrough && <WalkthroughCard stats={data.walkthrough} />}

      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Time on each page"
            tip="Per-path engagement: views, average dwell, exits from that page, and how often a view was followed by a trial."
          />
          <table>
            <thead>
              <tr>
                <th>Page</th>
                <th>Views</th>
                <th>Avg time</th>
                <th>Exits</th>
                <th>Then signed up</th>
              </tr>
            </thead>
            <tbody>
              {data.pages.length === 0 && (
                <tr><td colSpan={5} className="admin-muted">No page views yet.</td></tr>
              )}
              {data.pages.map((row) => (
                <tr key={row.path}>
                  <td><code>{row.path}</code></td>
                  <td>{row.views}</td>
                  <td>{formatDurationMs(row.avg_ms)}</td>
                  <td>{row.exits}</td>
                  <td>{row.signed_up_after}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <Cluster gap={3} align="center">
            <TipHeading
              label="Visitors"
              tip="Individual sessions matching the filters: when they landed, IP, country, path journey, dwell, and outcome."
            />
            <p className="admin-muted">{data.total} in this view · IP, country, path, and time</p>
          </Cluster>
          <table>
            <thead>
              <tr>
                <th>When (UTC)</th>
                <th>IP</th>
                <th>Country</th>
                <th>Path</th>
                <th>Time</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {data.sessions.length === 0 && (
                <tr><td colSpan={6} className="admin-muted">No visitors match these filters.</td></tr>
              )}
              {data.sessions.map((row) => (
                <tr key={row.session_id}>
                  <td>{formatUtc(row.started_at)}</td>
                  <td><code>{formatIp(row.source_ip)}</code></td>
                  <td>{countryLabel(row.country)}</td>
                  <td>
                    <Journey pages={row.pages} />
                    {(row.videos || []).map((video) => (
                      <p key={video.video} className="admin-muted">
                        Watched walkthrough: {video.completed ? "to the end" : `${video.max_pct}%`}
                        {` · ${formatDurationMs(video.watched_ms)}`}
                      </p>
                    ))}
                    {row.org_id ? (
                      <p className="admin-muted">
                        Trial: <Link to={`/customers/${row.org_id}`}>{row.org_id}</Link>
                      </p>
                    ) : null}
                  </td>
                  <td>{formatDurationMs(row.total_ms)}</td>
                  <td>
                    <span className={`admin-badge ${outcomeClass(row.outcome)}`}>
                      {OUTCOME_LABEL[row.outcome] || row.outcome}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Stack>
      </Card>
    </Stack>
  );
}

function WalkthroughCard({ stats }: { stats: WalkthroughStats }) {
  return (
    <Card>
      <Stack gap={3}>
        <Cluster gap={3} align="center">
          <TipHeading
            label="Who watched the walkthrough"
            tip="Visits that pressed play on the product walkthrough video (saleslox.com/#walkthrough). Furthest point is the latest position reached; time watched counts only seconds actually played, so skipping ahead does not inflate it."
          />
          <p className="admin-muted">
            {stats.viewers} played it · {stats.completed} watched to the end · {stats.signed_up} then started a trial
            {stats.viewers > 0 ? ` · average ${formatDurationMs(stats.avg_watched_ms)}, ${stats.avg_pct}% reached` : ""}
          </p>
        </Cluster>
        <table>
          <thead>
            <tr>
              <th>Played (UTC)</th>
              <th>IP</th>
              <th>Country</th>
              <th>Furthest point</th>
              <th>Time watched</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {stats.views.length === 0 && (
              <tr><td colSpan={6} className="admin-muted">Nobody has played the walkthrough in this view.</td></tr>
            )}
            {stats.views.map((row) => (
              <tr key={row.session_id}>
                <td>{formatUtc(row.played_at)}</td>
                <td><code>{formatIp(row.source_ip)}</code></td>
                <td>{countryLabel(row.country)}</td>
                <td>{row.completed ? "Watched to the end" : `${row.max_pct}%`}</td>
                <td>{formatDurationMs(row.watched_ms)}</td>
                <td>
                  <span className={`admin-badge ${outcomeClass(row.outcome)}`}>
                    {OUTCOME_LABEL[row.outcome] || row.outcome}
                  </span>
                  {row.org_id ? (
                    <p className="admin-muted">
                      Trial: <Link to={`/customers/${row.org_id}`}>{row.org_id}</Link>
                    </p>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Stack>
    </Card>
  );
}

function KpiTile({
  label,
  tip,
  value,
  hint,
}: {
  label: string;
  tip: string;
  value: number | string;
  hint: string;
}) {
  return (
    <Card>
      <Stack gap={2}>
        <TipKicker label={label} tip={tip} />
        <p className="admin-kpi">{value}</p>
        <p className="admin-muted">{hint}</p>
      </Stack>
    </Card>
  );
}

function HitsChart({ points }: { points: WebsiteOverview["hits_per_day"] }) {
  if (points.length === 0) {
    return <p className="admin-muted">No daily hits yet.</p>;
  }
  const max = Math.max(...points.map((point) => Math.max(point.pageviews, point.sessions)), 1);
  const barWidth = 100 / Math.max(points.length, 1);
  const labelEvery = points.length > 10 ? 3 : 1;
  return (
    <Stack gap={2}>
      <svg viewBox="0 0 100 60" className="admin-chart-svg" role="img" aria-label="Hits per day">
        {points.map((point, index) => {
          const height = (point.pageviews / max) * 38;
          const sessionHeight = (point.sessions / max) * 38;
          const x = index * barWidth + barWidth * 0.18;
          const label = point.day.slice(8);
          return (
            <g key={point.day}>
              <title>{`${point.day}: ${point.pageviews} page views, ${point.sessions} sessions`}</title>
              <rect x={x} y={46 - height} width={barWidth * 0.64} height={height} rx="0.8" className="admin-chart-bar" />
              <text
                x={x + (barWidth * 0.64) / 2}
                y={46 - height - 1.5}
                textAnchor="middle"
                className="admin-chart-value"
              >
                {point.pageviews > 0 ? point.pageviews : ""}
              </text>
              <rect
                x={x}
                y={46 - sessionHeight}
                width={barWidth * 0.28}
                height={sessionHeight}
                rx="0.8"
                className="admin-chart-bar admin-chart-bar--sessions"
              />
              {index % labelEvery === 0 && (
                <text x={index * barWidth + barWidth / 2} y="58" textAnchor="middle" className="admin-chart-label">
                  {label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <Cluster gap={3} align="center">
        <Cluster gap={1} align="center">
          <span className="admin-chart-swatch admin-chart-swatch--pageviews" />
          <span className="admin-muted">Page views</span>
        </Cluster>
        <Cluster gap={1} align="center">
          <span className="admin-chart-swatch admin-chart-swatch--sessions" />
          <span className="admin-muted">Sessions</span>
        </Cluster>
      </Cluster>
    </Stack>
  );
}

function PathList({ rows, empty }: { rows: PathGroup[]; empty: string }) {
  if (rows.length === 0) {
    return <p className="admin-muted">{empty}</p>;
  }
  return (
    <Stack gap={3}>
      {rows.map((row) => (
        <div key={row.pages.join(">")}>
          <Cluster gap={2} align="center">
            <span className={`admin-badge ${outcomeClass(row.outcome)}`}>{row.count}×</span>
            <span className="admin-muted">{formatDurationMs(row.avg_ms)} avg</span>
          </Cluster>
          <Journey
            pages={row.pages.map((path, index) => ({ path, duration_ms: row.durations_ms[index] || 0 }))}
          />
        </div>
      ))}
    </Stack>
  );
}

function Journey({ pages }: { pages: PageHit[] }) {
  if (!pages.length) return <span className="admin-muted">—</span>;
  return (
    <div className="admin-journey">
      {pages.map((page, index) => (
        <span key={`${page.path}-${index}`} className="admin-journey-step">
          <code>{page.path}</code>
          <span className="admin-muted"> {formatDurationMs(page.duration_ms)}</span>
          {index < pages.length - 1 ? <span className="admin-journey-arrow" aria-hidden="true"> → </span> : null}
        </span>
      ))}
    </div>
  );
}
